import { HandmadeBuilder, type HandmadePage, type HandmadeRankType } from './HandmadeBuilder.ts';
import { GoodsBuilder, type GoodsPage, type GoodsRankType } from './GoodsBuilder.ts';
import { EbookBuilder, type EbookPage, type EbookRankType } from './EbookBuilder.ts';
import { BrandDealBuilder, type BrandDealPage, type BrandDealRankType } from './BrandDealBuilder.ts';
import { CategoryLevel1_CODE } from '../../../../../../shared/mps-shared/domain/category/types.ts';

/**
 * 일반 상품 URL 빌더
 *
 * @returns {GoodsBuilder} 일반 상품 URL 빌더 인스턴스
 * @example
 * ```typescript
 * const builder = createGoodsUrlBuilder();
 *
 * // 랭킹 페이지 URL
 * const rankingUrl = builder.build();
 * // → '/kr/@/goods/ranking'
 *
 * // 월간 랭킹 URL
 * const monthlyRanking = builder.setRankType('MONTHLY').build();
 * // → '/kr/@/goods/ranking?type=monthly'
 *
 * // 상품 목록 페이지 URL
 * const productsUrl = builder.setPage('PRODUCTS').build();
 * // → '/kr/@/goods/products'
 *
 * // 고급 필터가 적용된 상품 목록 URL
 * const advancedFilterUrl = builder
 *   .setPage('PRODUCTS')
 *   .setFilters({
 *     cate_list_id: 1,
 *     min_price: 10000,
 *     max_price: 50000,
 *     style_filter_ids: [1, 2, 3],
 *     creator_filter_ids: [10, 20]
 *   })
 *   .setSort('newest')
 *   .build();
 * // → '/kr/@/goods/products?cate_list_id=1&min_price=10000&max_price=50000&style_filter_ids=1%2C2%2C3&creator_filter_ids=10%2C20&sort=newest'
 *
 * // 실시간 팔린 상품 URL
 * const latestSoldUrl = builder.setPage('LATEST_SOLD').build();
 * // → '/kr/@/realtime'
 * ```
 */
const createGoodsUrlBuilder = (): GoodsBuilder => {
  return new GoodsBuilder({
    lang: ET.lang,
    // TODO: @kjj 하드코딩 빼고 code 로 변경 필요
    cate_root_id: 1,
  });
};

const createEbookUrlBuilder = () => {
  return new EbookBuilder({
    lang: ET.lang,
    // TODO: @kjj 하드코딩 빼고 code 로 변경 필요
    cate_root_id: 4,
  });
};

const createHandmadeUrlBuilder = () => {
  return new HandmadeBuilder({
    lang: ET.lang,
    // TODO: @kjj 하드코딩 빼고 code 로 변경 필요
    cate_root_id: 2,
  });
};

const createBrandDealUrlBuilder = () => {
  return new BrandDealBuilder({
    lang: ET.lang,
    // TODO: @kjj 하드코딩 빼고 code 로 변경 필요
    cate_root_id: 3,
  });
};

export { GoodsPage, GoodsRankType, createGoodsUrlBuilder };

export { BrandDealPage, BrandDealRankType, createBrandDealUrlBuilder };

export { HandmadePage, HandmadeRankType, createHandmadeUrlBuilder };

export { EbookPage, EbookRankType, createEbookUrlBuilder };

export const createBuilderByCateRootCode = (
  cateRootCode: CategoryLevel1_CODE,
): GoodsBuilder | EbookBuilder | HandmadeBuilder | BrandDealBuilder => {
  if (cateRootCode === CategoryLevel1_CODE.HANDMADE) {
    /**
     *  handmade, brand-deal, ebook 상품은 너무 없을 경우를 대비해서 우선 monthly
     *  history: https://marpple.slack.com/archives/C05Q5T9TH8S/p1753164512932599?thread_ts=1753164483.971709&cid=C05Q5T9TH8S
     */
    return createHandmadeUrlBuilder().setRankType('MONTHLY');
  }
  if (cateRootCode === CategoryLevel1_CODE.BRAND_DEAL) {
    /**
     *  handmade, brand-deal, ebook 상품은 너무 없을 경우를 대비해서 우선 monthly
     *  history: https://marpple.slack.com/archives/C05Q5T9TH8S/p1753164512932599?thread_ts=1753164483.971709&cid=C05Q5T9TH8S
     */
    return createBrandDealUrlBuilder().setRankType('MONTHLY');
  }
  if (cateRootCode === CategoryLevel1_CODE.EBOOK) {
    /**
     *  handmade, brand-deal, ebook 상품은 너무 없을 경우를 대비해서 우선 monthly
     *  history: https://marpple.slack.com/archives/C05Q5T9TH8S/p1753164512932599?thread_ts=1753164483.971709&cid=C05Q5T9TH8S
     */
    return createEbookUrlBuilder().setRankType('MONTHLY');
  }

  /* 기본이 Goods */
  return createGoodsUrlBuilder();
};
